/**
 * Created by Ning Jiang on 2021-05-05.
 */

import {DeadwoodRip} from "../DeadwoodRip";
import {DeadwoodRipLayers, Layers} from "./DeadwoodRipLayers";
import {ShootableItem} from "./shooting/ShootableItem";
import {DeadwoodRipSound} from "../sound/DeadwoodRipSound";
import {GameMode} from "@nolimitcity/slot-game/bin/flowcontrol/GameMode";
import {gsap, TimelineLite} from "gsap";
import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";

export class DeadwoodRipBackground extends PIXI.Container {

    private _backgroundMainSprite:PIXI.Sprite;
    private _backgroundMain:ShootableItem;
    private _backgroundForeground:PIXI.Sprite;
    private _reelFrameShooting:ShootableItem;
    private _reelBg:PIXI.Sprite;
    private _reelFrame:PIXI.Sprite;
    private _reelFrameSpine:PIXI.spine.Spine;
    private _dimmer:PIXI.Graphics;
    private _sfsBgEffect:PIXI.spine.Spine | null;

    private static _stageShakeTl:TimelineLite | null;

    private _currentMode:string;
    public get currentMode() {
        return this._currentMode;
    }

    private _xbet:boolean;
    public get xbet():boolean {
        return this._xbet;
    }

    public set xbet(value:boolean) {
        this._xbet = value;
        this.updateMode(this._currentMode);
    }

    constructor() {
        super();
        this.init();
    }

    private init() {
        this._dimmer = new PIXI.Graphics();
        this._dimmer.beginFill(0x000000, 1);
        this._dimmer.drawRect(-840, -840, 1680, 1680);
        this._dimmer.endFill();
        this._dimmer.alpha = 0;
        this._dimmer.position.set(360, 360);

        this._backgroundMainSprite = new PIXI.Sprite();
        this._backgroundMainSprite.anchor.set(0.5, 0.5);
        this._backgroundMain = new ShootableItem({
                name : "background",
                holeScale : 0.8,
                view0 : this._backgroundMainSprite,
                soundPlayer : DeadwoodRipSound.getDefaultGunShotEmptyTimeline
            }
        );
        this._backgroundMain.position.set(360, 360);
        DeadwoodRipLayers.getLayer(Layers.BACKGROUND)?.addChild(this._backgroundMain);

        this._backgroundForeground = new PIXI.Sprite(DeadwoodRip.assetLoader.getTextures("reelframe/wheelbarrel")[0]);
        this._backgroundForeground.anchor.set(0.5);
        this._backgroundForeground.position.set(8, 592);
        DeadwoodRipLayers.getLayer(Layers.BACKGROUND_FOREGROUND)?.addChild(this._backgroundForeground);
        this.updateMode(GameMode.NORMAL);

        // shooting top bg pole with spark
        const bgTopPoleShootingHoleContainer = new PIXI.Container();
        DeadwoodRipLayers.getLayer(Layers.BACKGROUND)?.addChild(bgTopPoleShootingHoleContainer);
        const bgTopPoleShootingView = new PIXI.Graphics();
        bgTopPoleShootingView.beginFill(0x00ff00, 0.6);
        bgTopPoleShootingView.drawRect(172, -378, 74, 390);
        bgTopPoleShootingView.endFill();
        bgTopPoleShootingView.alpha = 0;
        const bgPoleShooting = new ShootableItem({
            name : "bgBottom",
            target : bgTopPoleShootingHoleContainer,
            view0 : bgTopPoleShootingView,
            soundPlayer : DeadwoodRipSound.getDefaultGunShotWoodTimeline,
            holeGetter : () => ShootableItem.getDefaultWoodHole(0.5),
            sparkGetter : () => ShootableItem.getDefaultWoodSpark(0.5)
        });
        DeadwoodRipLayers.getLayer(Layers.BACKGROUND)?.addChild(bgPoleShooting);

        // shooting bottom bg with spark
        const bgBottomShootingHoleContainer = new PIXI.Container();
        DeadwoodRipLayers.getLayer(Layers.BACKGROUND)?.addChild(bgBottomShootingHoleContainer);
        const bgBottomShootingView = new PIXI.Graphics();
        bgBottomShootingView.beginFill(0x00ff00, 0.6);
        bgBottomShootingView.drawRect(-360, 646, 1500, 450);
        bgBottomShootingView.endFill();
        bgBottomShootingView.alpha = 0;
        const bgBottomShooting = new ShootableItem({
            name : "bgBottom",
            target : bgBottomShootingHoleContainer,
            view0 : bgBottomShootingView,
            soundPlayer : DeadwoodRipSound.getDefaultGunShotWoodTimeline,
            sparkGetter : () => ShootableItem.getDefaultWoodSpark(0.3)
        });
        DeadwoodRipLayers.getLayer(Layers.BACKGROUND)?.addChild(bgBottomShooting);

        this._reelBg = new PIXI.Sprite(DeadwoodRip.assetLoader.getTextures("reelframe/reelbg")[0]);
        this._reelBg.anchor.set(0.5);
        this._reelBg.position.set(360, 360);
        DeadwoodRipLayers.getLayer(Layers.REEL_BACKGROUND)?.addChild(this._reelBg);

        // reel frame
        this._reelFrame = new PIXI.Sprite();
        this._reelFrame.anchor.set(0.5);
        this._reelFrame.position.set(354, 352);
        DeadwoodRipLayers.getLayer(Layers.FOREGROUND)?.addChild(this._reelFrame);

        this._reelFrameSpine = new PIXI.spine.Spine(DeadwoodRip.assetLoader.getSpineAsset("frame"));
        this._reelFrameSpine.autoUpdate = true;
        this._reelFrameSpine.position.set(this._reelFrame.x, this._reelFrame.y);
        DeadwoodRipLayers.getLayer(Layers.FOREGROUND)?.addChild(this._reelFrameSpine);
        this._reelFrameSpine.state.setAnimation(0, "main", false);

        const reelForegroundShootingHoleContainer = new PIXI.Container();
        reelForegroundShootingHoleContainer.position.set(this._reelFrame.x, this._reelFrame.y);
        DeadwoodRipLayers.getLayer(Layers.FOREGROUND_HOLE)?.addChild(reelForegroundShootingHoleContainer);
        const reelFrameShootingView = new PIXI.Graphics();
        reelFrameShootingView.beginFill(0xff0000, 0.6);
        reelFrameShootingView.drawRect(-404, -360, 50, 740); // left bar
        reelFrameShootingView.drawRect(366, -360, 50, 740); // right bar
        reelFrameShootingView.drawRect(-456, -342, 910, 56); // top bar
        reelFrameShootingView.drawRect(-212, 300, 462, 20); // bottom bar
        reelFrameShootingView.drawRect(226, 292, 24, 8); // bottom bar turn
        reelFrameShootingView.drawRect(222, -286, 144, 154); // top-right
        reelFrameShootingView.drawRect(226, 154, 140, 138); // bottom-right
        reelFrameShootingView.drawRect(-354, 226, 142, 106); // bottom-left
        reelFrameShootingView.moveTo(-354, -286);// top-left
        reelFrameShootingView.lineTo(-210, -286);
        reelFrameShootingView.lineTo(-206, -206);
        reelFrameShootingView.lineTo(-354, -196);
        reelFrameShootingView.endFill();
        reelFrameShootingView.alpha = 0;
        this._reelFrameShooting = new ShootableItem({
            name : "reelFrame",
            target : reelForegroundShootingHoleContainer,
            holeScale : 1,
            view0 : reelFrameShootingView,
            soundPlayer : DeadwoodRipSound.getDefaultGunShotWoodTimeline,
            holeGetter : () => ShootableItem.getDefaultWoodHole(1),
            sparkGetter : () => ShootableItem.getDefaultWoodSpark(1)
        });
        this._reelFrameShooting.position.set(this._reelFrame.x, this._reelFrame.y);
        // DeadwoodRipLayers.getLayer(Layers.FOREGROUND_HOLE)?.addChild(this._reelFrameShooting);
        DeadwoodRipLayers.getLayer(Layers.BACKGROUND)?.addChild(this._reelFrameShooting);

        // const vulture = new PIXI.Sprite(DeadwoodRip.assetLoader.getTextures("reelframe/vulture")[0]);
        // vulture.anchor.set(0.5, 0.88);
        // vulture.position.set(100, 16);
        // vulture.scale.set(0.8);
        // DeadwoodRipLayers.getLayer(Layers.FOREGROUND)?.addChild(vulture);

        this.onOrientationChanged();
        this.onResize();
    }

    public onOrientationChanged() {
        // TODO: check restore, mode, orientation
    }

    public onResize() {
        // TODO: check restore, mode, orientation
    }

    public updateMode(mode:string):void {
        this._currentMode = mode;
        let textureName = `background_${mode}`;
        if(this._xbet && this._currentMode === GameMode.NORMAL) {
            textureName += `_XBET`;
        }

        this._backgroundMainSprite.texture = DeadwoodRip.assetLoader.getTextures(textureName)[0];
        this._backgroundForeground.visible = mode === GameMode.NORMAL;

        if(mode.includes("FREESPIN")) {
            if(this._sfsBgEffect) {
                return;
            }
            this._sfsBgEffect = new PIXI.spine.Spine(DeadwoodRip.assetLoader.getSpineAsset("mainIdleDust"));
            this._sfsBgEffect.autoUpdate = true;
            // this._sfsBgEffect.alpha = 0;
            this._sfsBgEffect.position.set(360, 360);
            DeadwoodRipLayers.getLayer(Layers.BACKGROUND_IDLE)?.addChild(this._sfsBgEffect);
            this._sfsBgEffect.state.setAnimation(0, "animation", true);
        } else {
            if(this._sfsBgEffect) {
                this._sfsBgEffect.state.setEmptyAnimations(0);
                this._sfsBgEffect.parent.removeChild(this._sfsBgEffect);
                this._sfsBgEffect.destroy();
                this._sfsBgEffect = null;
            }
        }
    }

    public static moveStage(tl:TimelineLite):void {
        this.stopStageMove();
        tl.play();
        this._stageShakeTl = tl;
    }

    public static stopStageMove():void {
        if(this._stageShakeTl && this._stageShakeTl.isActive()) {
            this._stageShakeTl.progress(1);
        }
        this._stageShakeTl = null;
        DeadwoodRipBackground.stage.position.set(0, 0);
    }

    public static get stage():PIXI.Container {
        return DeadwoodRipLayers.getLayer(Layers.BACKGROUND)!.parent!;
    }

    public static shakeStageOnce(factor?:number, timeScale?:number):void {
        if(!factor) {
            factor = 1;
        }
        const tl = new TimelineLite({paused : true});
        tl.add(gsap.to(DeadwoodRipBackground.stage, {x : 16 * factor, y : -8 * factor, duration : 0.033}));
        tl.add(gsap.to(DeadwoodRipBackground.stage, {x : 0, y : 0, duration : 0.267}));
        if(timeScale) {
            tl.timeScale(timeScale);
        }
        DeadwoodRipBackground.moveStage(tl);
    }

    public dimScreen(alpha:number, duration:number, layer?:StageLayer):TimelineLite {
        const tl = new TimelineLite();
        if(layer) {
            tl.add(() => {layer.addChildAt(this._dimmer, 0)});
        }
        tl.add(gsap.to(this._dimmer, duration, {alpha : alpha}));
        return tl;
    }

    public playShotEffects():void {
        if(this._currentMode === GameMode.NORMAL && !DeadwoodRip.background.xbet) {
            return;
        }

        const aniName = this._currentMode === GameMode.NORMAL && DeadwoodRip.background.xbet ? "XBET" : this._currentMode;
        const spine = new PIXI.spine.Spine(DeadwoodRip.assetLoader.getSpineAsset("bgEffect"));
        spine.autoUpdate = true;
        spine.position.set(360, 360);
        DeadwoodRipLayers.getLayer(Layers.BACKGROUND_FOREGROUND)?.addChild(spine);
        spine.state.setAnimation(0, aniName, false);
        const tl = new TimelineLite();
        tl.add(() => {
            spine.state.setEmptyAnimations(0);
            spine.parent.removeChild(spine);
            spine.destroy();
        }, spine.spineData.findAnimation(aniName).duration);
    }

}